import { useState } from "react";
import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
  QueryConstraint,
} from "firebase/firestore";
import * as XLSX from "xlsx";
import { db } from "../../firebase";
import { showErrorToast, showSuccessToast } from "../../toastmsg";

interface ExportExcelButtonProps<T> {
  collectionName: string;
  baseConstraints?: QueryConstraint[];
  filters?: Record<string, unknown>;
  sortField?: string;
  sortOrder?: "asc" | "desc";
  filename: string;
  sheetName?: string;
  mapRow: (item: T, index: number) => Record<string, unknown>;
  userRole?: string | null;
  disabled?: boolean;
}

const ExportExcelButton = <T,>({
  collectionName,
  baseConstraints = [],
  filters = {},
  sortField = "createdDate",
  sortOrder = "desc",
  filename,
  sheetName = "Leads",
  mapRow,
  userRole,
  disabled = false,
}: ExportExcelButtonProps<T>) => {
  const [isExporting, setIsExporting] = useState(false);

  if (userRole !== "admin" && userRole !== "super admin") return null;

  const handleExport = async () => {
    if (isExporting) return;

    setIsExporting(true);
    try {
      const filterConstraints = Object.entries(filters)
        .filter(([, value]) => value !== "" && value !== null && value !== undefined)
        .map(([field, value]) => where(field, "==", value));

      const exportQuery = query(
        collection(db, collectionName),
        ...baseConstraints,
        ...filterConstraints,
        orderBy(sortField, sortOrder),
      );
      const snapshot = await getDocs(exportQuery);

      if (snapshot.empty) {
        showErrorToast("No data available to export");
        return;
      }

      const rows = snapshot.docs.map((document, index) =>
        mapRow(
          {
            ID: document.id,
            id: document.id,
            ...document.data(),
          } as T,
          index,
        ),
      );
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const headers = Object.keys(rows[0]);

      worksheet["!autofilter"] = {
        ref: XLSX.utils.encode_range({
          s: { r: 0, c: 0 },
          e: { r: rows.length, c: Math.max(headers.length - 1, 0) },
        }),
      };
      worksheet["!cols"] = headers.map((header) => {
        const longestValue = rows.reduce((maxLength, row) => {
          const value = row[header];
          return Math.max(maxLength, String(value ?? "").length);
        }, header.length);

        return { wch: Math.min(Math.max(longestValue + 2, 12), 50) };
      });

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName.slice(0, 31));
      XLSX.writeFile(workbook, filename);
      showSuccessToast(`Exported ${rows.length} records successfully`);
    } catch (error) {
      console.error("Failed to export Excel file:", error);
      showErrorToast("Failed to export Excel file");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleExport}
      disabled={disabled || isExporting}
      className="cursor-pointer rounded-md border bg-blue-500 px-4 py-2 text-sm text-white shadow-sm transition-colors hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {isExporting ? "Exporting..." : "Export Excel"}
    </button>
  );
};

export default ExportExcelButton;
